/**
 * Rooms Modal Component
 * Room selection interface for the nightclub
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { DoorOpen, Star, Lock, X, Users } from 'lucide-react';

const RoomsModal = ({
  isOpen,
  onClose,
  rooms,
  currentRoom,
  onChangeRoom,
  isAdmin,
  canAccessRooftop,
  user,
  hasVipAccess,
  roomCounts = {}
}) => {
  if (!isOpen) return null;

  const checkRoomAccess = (room) => {
    if (room.id === 'rooftop') {
      return canAccessRooftop;
    } else if (room.id === 'vip_lounge') {
      return isAdmin || user?.is_vip === true || hasVipAccess;
    } else if (room.id === 'backstage') {
      return isAdmin || user?.role === 'artist' || user?.backstage_access === true || hasVipAccess;
    } else if (!room.isVIP) {
      return true;
    } else {
      return room.requiredRole && room.requiredRole.includes(user?.role);
    }
  };

  const accessHint = (room) => {
    if (room.id === 'rooftop') return 'admin / VIP / rooftop pass';
    if (room.id === 'vip_lounge') return 'admin / VIP';
    if (room.id === 'backstage') return 'admin / artist / backstage pass';
    if (room.requiredRole) return room.requiredRole.join(' / ');
    return 'special access';
  };

  return (
    <div 
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      data-testid="rooms-modal-overlay"
    >
      <Card 
        className="w-full max-w-md bg-gray-900 border-cyan-500/30 max-h-[90vh] overflow-y-auto"
        data-testid="rooms-modal"
      >
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
          <CardTitle className="text-white flex items-center gap-2">
            <DoorOpen className="w-5 h-5 text-cyan-500" />
            Club Rooms
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-rooms-modal">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {rooms.map(room => {
            const hasAccess = checkRoomAccess(room);
            const isCurrentRoom = currentRoom === room.id;
            
            return (
              <button
                key={room.id}
                onClick={() => onChangeRoom(room)}
                disabled={isCurrentRoom || !hasAccess}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  isCurrentRoom 
                    ? 'border-cyan-500 bg-cyan-500/20' 
                    : hasAccess
                      ? 'border-white/20 hover:border-cyan-500/50 hover:bg-cyan-500/10'
                      : 'border-red-500/30 bg-red-500/5 opacity-70'
                }`}
                data-testid={`room-${room.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{room.name}</span>
                      {room.isVIP && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" /> VIP
                        </span>
                      )}
                      {room.accessViaElevator && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-sky-500/20 text-sky-400 rounded flex items-center gap-1">
                          🏙️ Rooftop
                        </span>
                      )}
                      {isCurrentRoom && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/30 text-cyan-300 rounded">
                          You're here
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/60 mt-1">{room.description}</p>
                    {room.isVIP && !hasAccess && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Requires: {accessHint(room)}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex flex-col items-end gap-1">
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-green-400" />
                      <span className="text-xs text-green-400 font-medium">{roomCounts[room.id] || 0}</span>
                    </div>
                    <span className="text-[10px] text-white/30">/ {room.maxPlayers}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default RoomsModal;
